import React from "react";
import {
  Box,
  Text,
  InputGroup,
  InputLeftElement,
  Input,
  Stack,
  Flex,
  HStack,
  Button,
  Tag,
  VStack,
  Icon,
  Skeleton,
} from "@chakra-ui/react";
import { FiSearch, FiPlus, FiClock, FiRefreshCw } from "react-icons/fi";
import AddedMedicineList from "./AddedMedicineList";
import { CategoryPill, MedicineImage, StockDot } from "../../UI/Primitives";
import { colors, gradients, shadows } from "../../UI/designSystem";

const MedicineSearchCard = ({
  searchTerm,
  isSearching,
  searchResults,
  handleSearchChange,
  handleAddMedicine,
  addedMeds,
  setAddedMeds,
  isPrescriptionRequired,
  hideSearch = false,
}) => {
  const getName = (med) => med.product_name || med.Name || med.name || "Unnamed";
  const getDesc = (med) => med.composition || med.key_ingredients || "";
  const getManufacturer = (med) => med.manufacturer || med.company || med.marketer || med.Manufacturer || "";
  const getPackSize = (med) => med.pack_size || med.packSize || med.PackSize || med.unit || "";
  const getPrice = (med) =>
    med.mrp !== undefined && med.mrp !== null ? Number(med.mrp).toFixed(2) : null;
  const quickSuggestions = [
    { name: "Dolo 650", meta: "Fever & pain", tint: "#cbd5e1" },
    { name: "Paracetamol", meta: "Generic", tint: "#cbd5e1" },
    { name: "Cetirizine", meta: "Allergy", tint: "#cbd5e1" },
    { name: "Azithromycin", meta: "Antibiotic", tint: "#cbd5e1" },
    { name: "ORS", meta: "Hydration", tint: "#cbd5e1" },
  ];

  return (
    <Box position="relative">
      {!hideSearch && (
      <VStack align="stretch" spacing={2.5} mb={2}>
        <InputGroup size={{ base: "md", lg: "sm", xl: "md" }}>
          <InputLeftElement pointerEvents="none">
            <FiSearch color="#94a3b8" />
          </InputLeftElement>
          <Input
            data-medicine-search-input
            placeholder="Search medicines by brand or generic salt"
            value={searchTerm}
            onChange={(e) => handleSearchChange(e.target.value)}
            bg="white"
            borderRadius={{ base: "16px", lg: "14px", xl: "16px" }}
            boxShadow="0 14px 28px rgba(15,23,42,0.06)"
            borderColor="#dbe3ef"
          />
        </InputGroup>
        {searchTerm && searchTerm.length < 2 && (
          <Text fontSize="xs" color="#94a3b8" m={0} pl={1}>Type at least 2 characters to search</Text>
        )}
        {!searchTerm && !addedMeds?.length && (
          <Flex gap={2} flexWrap="wrap">
            {quickSuggestions.map((item) => (
              <Box
                key={item.name}
                as="button"
                px={3}
                py={2.5}
                borderRadius="14px"
                cursor="pointer"
                transition="all 0.12s ease"
                bg="white"
                border="1px solid #e8edf3"
                boxShadow={shadows.xs}
                _hover={{ bg: "#f8fafc", color: "#0f172a", transform: "translateY(-1px)" }}
                onClick={() => handleSearchChange(item.name)}
              >
                <HStack spacing={2}>
                  <Box w="10px" h="10px" borderRadius="full" bg={item.tint} border="1px solid rgba(15,23,42,0.08)" />
                  <Box textAlign="left">
                    <Text fontSize="12px" fontWeight="800" color="#0f172a" m={0}>{item.name}</Text>
                    <Text fontSize="10px" color="#64748b" m={0}>{item.meta}</Text>
                  </Box>
                </HStack>
              </Box>
            ))}
          </Flex>
        )}
      </VStack>
      )}

      {!hideSearch && isSearching && (
        <Stack spacing={3} mt={3}>
          {[1, 2].map((i) => (
            <Skeleton
              key={i}
              height="92px"
              borderRadius="14px"
              startColor="#fef2f2"
              endColor="#fee2e2"
            />
          ))}
        </Stack>
      )}

      {!hideSearch && !isSearching && (searchResults?.length ?? 0) > 0 && (
        <Box
          mt={3}
          bg="white"
          border={`1px solid ${colors.neutral[200]}`}
          borderRadius="14px"
          boxShadow={shadows.sm}
          maxH="360px"
          overflowY="auto"
          overflowX="hidden"
        >
          <Stack spacing={0} divider={<Box borderBottom={`1px solid ${colors.neutral[100]}`} />}>
            {searchResults.map((med) => {
              const requiresRx = isPrescriptionRequired(med);
              const price = getPrice(med);
              const desc = getDesc(med);
              return (
                <Flex
                  key={med._id || med.id || med.product_id || med.productId}
                  align="center"
                  gap={3}
                  p={3}
                  _hover={{ bg: colors.neutral[50] }}
                  transition="background .15s ease"
                >
                  <Box flex="1" minW="0">
                    <HStack spacing={2} mb={1} wrap="wrap">
                      <Text
                        fontWeight="800"
                        color={colors.neutral[900]}
                        fontSize="sm"
                        noOfLines={1}
                        title={getName(med)}
                        m={0}
                      >
                        {getName(med)}
                      </Text>
                      {requiresRx && (
                        <Tag size="sm" colorScheme="red" borderRadius="full" fontWeight="700" fontSize="10px">
                          Rx
                        </Tag>
                      )}
                    </HStack>
                    {desc ? (
                      <Text fontSize="12px" color={colors.neutral[500]} m={0} noOfLines={1}>
                        {desc}
                      </Text>
                    ) : null}
                    {(getPackSize(med) || getManufacturer(med)) ? (
                      <Text fontSize="11px" color={colors.neutral[400]} m={0} mt={0.5} noOfLines={1}>
                        {[getPackSize(med), getManufacturer(med)].filter(Boolean).join(" · ")}
                      </Text>
                    ) : null}
                    {price ? (
                      <Text fontSize="13px" fontWeight="800" color={colors.neutral[900]} mt={1.5} m={0}>
                        ₹{price}
                      </Text>
                    ) : null}
                  </Box>
                  <Button
                    size="sm"
                    bgGradient={gradients.brand}
                    color="white"
                    leftIcon={<FiPlus />}
                    borderRadius="full"
                    px={4}
                    flexShrink={0}
                    _hover={{ bgGradient: gradients.brandBold }}
                    onClick={() => handleAddMedicine(med)}
                  >
                    Add
                  </Button>
                </Flex>
              );
            })}
          </Stack>
        </Box>
      )}

      {!hideSearch && !isSearching && searchTerm && searchTerm.length >= 2 && (!searchResults || searchResults.length === 0) && (
        <Box
          mt={3}
          bg="white"
          border={`1px solid ${colors.neutral[200]}`}
          borderRadius="14px"
          boxShadow={shadows.sm}
          p={4}
          textAlign="center"
        >
          <Text fontWeight="800" color={colors.neutral[900]} mb={1} fontSize="sm">
            No matches yet
          </Text>
          <Text fontSize="xs" color={colors.neutral[500]} m={0}>
            Try a different spelling or add another medicine later.
          </Text>
        </Box>
      )}

      <AddedMedicineList
        addedMeds={addedMeds}
        isPrescriptionRequired={isPrescriptionRequired}
        setAddedMeds={setAddedMeds}
      />
    </Box>
  );
};

export default MedicineSearchCard;
